/**
 * cameraView - resolves which POV renders the session.
 *
 *  - 'first' : the historical eye camera (the player UniversalCamera renders
 *              directly; no self-avatar).
 *  - 'third' : a derived chase camera behind the player + a visible self-pod
 *              (src/entities/ChaseCameraRig.js / LocalAvatar.js).
 *
 * The choice persists in settingsStore under 'cameraView' (switchable in the
 * settings Controls tab and via the V hotkey). Mirrors controlScheme.js: this
 * module is the only reader that interprets the raw setting.
 */

import { getSetting, setSetting, subscribe } from '../utils/settingsStore.js';
import { isUiBlocked } from '../state/uiPanelsStore';

/** @typedef {'first'|'third'} CameraView */

/** @returns {CameraView} the POV that should render right now */
export function getCameraView() {
  return getSetting('cameraView') === 'third' ? 'third' : 'first';
}

/**
 * Persist a POV choice ('first' | 'third'). Ignores anything else.
 * @param {string} view
 */
export function setCameraView(view) {
  if (view !== 'first' && view !== 'third') return;
  setSetting('cameraView', view);
}

/** Flip between first and third person. @returns {CameraView} the new view */
export function toggleCameraView() {
  const next = getCameraView() === 'third' ? 'first' : 'third';
  setCameraView(next);
  return next;
}

/**
 * Subscribe to POV changes. Fires with the resolved view.
 * @param {(view: CameraView) => void} fn
 * @returns {() => void} unsubscribe
 */
export function onCameraViewChange(fn) {
  return subscribe((key) => {
    if (key === 'cameraView') fn(getCameraView());
  });
}

/**
 * Install the global V hotkey that toggles the POV. Inert while typing in a
 * field or while a scene-blocking panel (menu/chat/editor/breakouts) is open.
 * Call once at app boot.
 * @returns {() => void} uninstall
 */
export function installCameraViewHotkey() {
  /** @param {KeyboardEvent} e */
  const onKeyDown = (e) => {
    if (e.key !== 'v' && e.key !== 'V') return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const target = /** @type {HTMLElement|null} */ (e.target);
    const tag = target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
    if (isUiBlocked()) return;
    toggleCameraView();
  };
  window.addEventListener('keydown', onKeyDown);
  return () => window.removeEventListener('keydown', onKeyDown);
}
